import { inject, Injectable } from '@angular/core';
import { FileTypes } from '@dntlab/share-files-dialog';
import { TranslateService } from '@ngx-translate/core';
import { getExtensionFromFileName, hasValidFileExtension, joinFields } from 'src/app/common/app.helpers';
import FieldsConstants from 'src/app/common/fields.constants';
import FieldAllowedValueClasses from 'src/app/forms/fields/field-allowed-value-classes.values';
import { FileDescriptor } from 'src/app/swagger-api';
import { isValidFileSize } from '../../common/file.helpers';
import { ActivityLog, FileCategories } from './files-manager.enum';

@Injectable({ providedIn: 'root' })
export class FilesValidationHandler {
  private translateService = inject(TranslateService);

  public allowedFileExtensions: string[] = [
    ...FieldsConstants.ALLOWED_FILE_MANAGER_DOCUMENTS_EXTENSIONS,
    ...FieldsConstants.ALLOWED_FILE_MANAGER_IMAGE_EXTENSIONS,
    ...FieldsConstants.ALLOWED_FILE_MANAGER_VIDEO_EXTENSIONS,
    ...FieldsConstants.ALLOWED_FILE_MANAGER_AUDIO_EXTENSIONS,
  ];

  public multipleFilesExtensions: string = joinFields([this.allowedFileExtensions], ', ');

  private contentTypeMapper: { [key in string]: FileTypes } = {
    application: FileTypes.DOCUMENT,
    image: FileTypes.IMAGE,
    video: FileTypes.VIDEO,
    audio: FileTypes.AUDIO,
  };

  private extensionsMapper: { [key in FileCategories | ActivityLog]: string[] } = {
    document: FieldsConstants.ALLOWED_FILE_MANAGER_DOCUMENTS_EXTENSIONS,
    image: FieldsConstants.ALLOWED_FILE_MANAGER_IMAGE_EXTENSIONS,
    video: FieldsConstants.ALLOWED_FILE_MANAGER_VIDEO_EXTENSIONS,
    audio: FieldsConstants.ALLOWED_FILE_MANAGER_AUDIO_EXTENSIONS,
    activities: null,
  };

  /**
   * @description Returns the allowed file extensions in format for input field depend on
   *              attribute metadata valueClassFront value.
   * @param { FieldAttribute } attr
   * @returns { FileUploadStrategy }
   */
  public getAcceptedFilesExtensions(valueClassFront: string): string {
    return {
      [FieldAllowedValueClasses.FILE]: '.png, .jpg, .jpeg, .gif',
      [FieldAllowedValueClasses.PHOTO]: '.png, .jpg, .jpeg, .gif',
      [FieldAllowedValueClasses.MULTIPLE_FILES]: this.multipleFilesExtensions,
    }[valueClassFront];
  }

  /**
   * @description Returns an ID of string resources as description for found
   *              validation error.
   * @param { File } file
   * @returns { string }
   */
  public getFileValidationDescription(file: File): string {
    if (!isValidFileSize(file.size)) {
      return this.translateService.instant('FILE_MANAGER_UPLOAD_FILE_INVALID_SIZE_DESCRIPTION', {
        filesName: file.name,
      }) as string;
    }

    if (!hasValidFileExtension(file, this.allowedFileExtensions)) {
      return this.translateService.instant('FILE_MANAGER_UPLOAD_FILE_INVALID_EXTENSION_DESCRIPTION', {
        filesName: file.name,
      }) as string;
    }

    return '';
  }

  /**
   * @description Returns files type.
   *     1. If the file content type isn't empty, uses it for type detection.
   *     2. Otherwise detects file type by extension fetched from its name.
   * @param { FileDescriptor } file
   * @returns { FileTypes }
   */
  public getFileType(file: FileDescriptor): FileTypes {
    if (file.content_type) {
      const type: string = file.content_type.split('/')[0];
      if (type) {
        return this.contentTypeMapper[type];
      }
    }

    return this.getFileTypeByItsName(file.name);
  }

  /**
   * @description Сyclically checks the files type by category. In case no category
   *              satisfies the condition returns basic FileTypes.DOCUMENT value.
   * @param { string } fileName
   * @returns { FileTypes }
   */
  public getFileTypeByItsName(fileName: string): FileTypes {
    const fileExtension: string = getExtensionFromFileName(fileName);

    if (this.isIncludeExtension(FileCategories.audio, fileExtension)) {
      return FileTypes.AUDIO;
    }

    if (this.isIncludeExtension(FileCategories.video, fileExtension)) {
      return FileTypes.VIDEO;
    }

    if (this.isIncludeExtension(FileCategories.images, fileExtension)) {
      return FileTypes.IMAGE;
    }

    return FileTypes.DOCUMENT;
  }

  /**
   * @description Checks if the file has allowed extension for specified category.
   * @param { File } file
   * @param { FileCategories } category
   * @returns { boolean }
   */
  public isAllowedFileExtension(file: FileDescriptor, category: FileCategories): boolean {
    const fileExtension: string = getExtensionFromFileName(file.name);

    return this.isIncludeExtension(category, fileExtension);
  }

  /**
   * @description Checks if the file extension is included in allowed extensions list
   *              for specified category.
   * @param { FileCategories } category
   * @param { string } fileExtension
   * @returns { boolean }
   */
  private isIncludeExtension(category: FileCategories, fileExtension: string): boolean {
    return this.extensionsMapper[category].some((extension: string) => extension === fileExtension);
  }
}
